import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import {
  PROCESSING_RESULT_EDITOR_REGEN_ERROR_EVENT,
  PROCESSING_RESULT_EDITOR_REGEN_FINISHED_EVENT,
  dispatchCancelableProcessingResultEditorEvent,
} from "../lib/processing_result_editor_events"

export default class extends Controller {
  static values = { id: Number }
  static targets = ["archiveSpinner"]

  connect() {
    this.consumer = createConsumer()
    this.subscription = this.consumer.subscriptions.create(
      { channel: "ProcessingResultChannel", processing_result_id: this.idValue },
      {
        connected: () => this.requestArchiveStatus(),
        received: (data) => this.handleMessage(data)
      }
    )
  }

  // Архив мог собраться раньше, чем страница подписалась на канал, —
  // тогда спиннер ZIP висел бы до ручной перезагрузки (ArchiveStatusAction).
  requestArchiveStatus() {
    if (this.hasArchiveSpinnerTarget) this.subscription?.perform("archive_status")
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  handleMessage(data) {
    console.log("Processing result message received:", data)
    
    if (data.type === "archive_status" && (data.state === "ready" || data.state === "error")) {
      console.log("Reloading page due to archive status...")
      window.location.reload()
    } else if (data.type === "regeneration_started") {
      console.log("Regeneration started...")
      this.showLoading("Идет перегенерация...", "Пожалуйста, подождите...")
    } else if (data.type === "regeneration_finished") {
      console.log("Regeneration finished")
      const shouldReload = dispatchCancelableProcessingResultEditorEvent(
        PROCESSING_RESULT_EDITOR_REGEN_FINISHED_EVENT,
        {
          processingResult: data.processing_result,
          complete: () => this.hideLoading(),
        }
      )

      if (shouldReload) {
        console.log("Reloading page after regeneration...")
        window.location.reload()
      }
    } else if (data.type === "regeneration_error") {
      console.error("Regeneration error:", data.error)
      const shouldReload = dispatchCancelableProcessingResultEditorEvent(
        PROCESSING_RESULT_EDITOR_REGEN_ERROR_EVENT,
        {
          error: data.error,
          complete: () => this.hideLoading(),
        }
      )

      if (shouldReload) {
        alert("Ошибка при перегенерации: " + data.error)
        this.hideLoading()
        window.location.reload()
      }
    } else if (data.type === "production_canvas_started") {
      console.log("Production canvas generation started...")
      this.showLoading("Генерируется производственный холст", "Пожалуйста, подождите")
    } else if (data.type === "production_canvas_finished") {
      console.log("Production canvas generation finished, reloading page...")
      window.location.reload()
    } else if (data.type === "production_canvas_error") {
      console.error("Production canvas generation error:", data.error)
      alert("Ошибка при генерации производственного холста: " + data.error)
      this.hideLoading()
      window.location.reload()
    } else if (data.type === "control_list_started") {
      console.log("Control list generation started...")
      this.showLoading("Генерируется контрольный лист", "Пожалуйста, подождите")
    } else if (data.type === "control_list_finished") {
      console.log("Control list generation finished, reloading page...")
      window.location.reload()
    } else if (data.type === "control_list_error") {
      console.error("Control list generation error:", data.error)
      alert("Ошибка при генерации контрольного листа: " + data.error)
      this.hideLoading()
      window.location.reload()
    } else if (data.type === "sticker_started") {
      console.log("Sticker generation started...")
      this.showLoading("Генерируется этикетка", "Пожалуйста, подождите")
    } else if (data.type === "sticker_finished") {
      console.log("Sticker generation finished, reloading page...")
      window.location.reload()
    } else if (data.type === "sticker_error") {
      console.error("Sticker generation error:", data.error)
      alert("Ошибка при генерации этикетки: " + data.error)
      this.hideLoading()
      window.location.reload()
    } else if (data.type === "production_pallete_started") {
      console.log("Production palette generation started...")
      this.showLoading("Генерируется производственная палитра", "Пожалуйста, подождите")
    } else if (data.type === "production_pallete_finished") {
      console.log("Production palette generation finished, reloading page...")
      window.location.reload()
    } else if (data.type === "production_pallete_error") {
      console.error("Production palette generation error:", data.error)
      alert("Ошибка при генерации производственной палитры: " + data.error)
      this.hideLoading()
      window.location.reload()
    }
  }

  showLoading(title, subtitle) {
    // Check if we already have an overlay
    if (document.getElementById("regen-loading-overlay")) return

    const overlay = document.createElement("div")
    overlay.id = "regen-loading-overlay"
    overlay.style.position = "fixed"
    overlay.style.top = "0"
    overlay.style.left = "0"
    overlay.style.width = "100%"
    overlay.style.height = "100%"
    overlay.style.backgroundColor = "rgba(255, 255, 255, 0.7)"
    overlay.style.zIndex = "9999"
    overlay.style.display = "flex"
    overlay.style.flexDirection = "column"
    overlay.style.alignItems = "center"
    overlay.style.justifyContent = "center"
    
    overlay.innerHTML = `
      <div class="spinner-border text-primary mb-3" role="status" style="width: 3rem; height: 3rem;">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div class="h4">${title}</div>
      <div class="text-muted">${subtitle}</div>
    `
    document.body.appendChild(overlay)
  }

  hideLoading() {
    document.getElementById("regen-loading-overlay")?.remove()
  }
}
